import type { IdvSendTiming } from '@common/enums';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Chip, Stack, Typography } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { warningSnackbar } from '@/utils/snackbar';
import ClientCard from './ClientCard';
import {
    type ClientDetailsEntry,
    type ClientDetailsErrors,
    getClientDetailsErrors,
    hasClientDetailsErrors,
} from './clientEntries';
import NewProspectStep from './NewProspectStep';

/** The primary client plus up to nine more */
const MAX_ADDITIONAL_CLIENTS = 9;

// Edits that change whether another field's error still applies. The cell number is checked against
// its country, and only needed when ID verification is sent automatically
const AFFECTED_ERRORS: Partial<Record<keyof ClientDetailsEntry, keyof ClientDetailsErrors>> = {
    phoneCountryCode: 'phoneNumber',
    idvSendTiming: 'phoneNumber',
};

interface Props {
    primaryClientDetails: ClientDetailsEntry;
    setPrimaryClientDetails: Dispatch<SetStateAction<ClientDetailsEntry>>;
    primaryClientDetailsErrors: ClientDetailsErrors;
    clientDetails: ClientDetailsEntry[];
    setClientDetails: Dispatch<SetStateAction<ClientDetailsEntry[]>>;
    clientDetailsErrors: ClientDetailsErrors[];
    defaultIdvSendTiming: IdvSendTiming | null;
    addClientRow(): void;
    resetErrors(): void;
}

export default function ClientDetails({
    primaryClientDetails,
    setPrimaryClientDetails,
    primaryClientDetailsErrors,
    clientDetails,
    setClientDetails,
    clientDetailsErrors,
    defaultIdvSendTiming,
    addClientRow,
    resetErrors,
}: Props) {
    // Clears the shown errors once a field that has one, or that affects one, is edited
    const resetErrorsFor = (
        errors: ClientDetailsErrors | undefined,
        patch: Partial<ClientDetailsEntry>,
    ) => {
        const fields = Object.keys(patch) as (keyof ClientDetailsEntry)[];
        const hasAffectedError = fields.some((field) => {
            const affected = AFFECTED_ERRORS[field];
            return errors?.[field as keyof ClientDetailsErrors] || (affected && errors?.[affected]);
        });
        if (hasAffectedError) {
            resetErrors();
        }
    };

    const updatePrimaryClient = (patch: Partial<ClientDetailsEntry>) => {
        setPrimaryClientDetails((prev) => ({ ...prev, ...patch }));
        resetErrorsFor(primaryClientDetailsErrors, patch);
    };

    const updateClient = (index: number, patch: Partial<ClientDetailsEntry>) => {
        setClientDetails((prev) =>
            prev.map((client, i) => (i === index ? { ...client, ...patch } : client)),
        );
        resetErrorsFor(clientDetailsErrors[index], patch);
    };

    const removeClient = (index: number) => {
        setClientDetails((prev) => prev.filter((_, i) => i !== index));
        resetErrors();
    };

    const isComplete = [primaryClientDetails, ...clientDetails].every(
        (client) => !hasClientDetailsErrors(getClientDetailsErrors(client)),
    );

    return (
        <NewProspectStep
            stepNumber="2"
            title="Client Information"
            description="Each client gets the intake email. Add a cell number to send ID verification automatically."
            isComplete={isComplete}
        >
            <Stack gap={2}>
                <Stack
                    direction="row"
                    gap={1.5}
                    alignItems="center"
                    sx={{
                        border: '1px solid',
                        borderColor: 'success.S_100',
                        borderRadius: 2,
                        bgcolor: 'success.S_50',
                        px: 2,
                        py: 1,
                    }}
                >
                    <Chip label="New!" size="small" color="success" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Automatically send identity verification requests through Treefort with your
                        prospect
                    </Typography>
                </Stack>
                <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    sx={{
                        border: '1px solid',
                        borderColor: 'neutral.N_100',
                        borderRadius: 2,
                        bgcolor: 'neutral.N_50',
                        px: 2,
                        py: 1,
                    }}
                >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: 'neutral.N_500' }} />
                    <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                        Treefort ID verification is an additional charge per client.
                    </Typography>
                </Stack>
                <ClientCard
                    title="Primary client"
                    client={primaryClientDetails}
                    defaultIdvSendTiming={defaultIdvSendTiming}
                    errors={primaryClientDetailsErrors}
                    onChange={updatePrimaryClient}
                />
                {clientDetails.map((client, index) => (
                    <ClientCard
                        key={client.rowId}
                        title={`Client ${index + 2}`}
                        client={client}
                        defaultIdvSendTiming={defaultIdvSendTiming}
                        errors={clientDetailsErrors[index]}
                        onChange={(patch) => updateClient(index, patch)}
                        onRemove={() => removeClient(index)}
                    />
                ))}
                <Button
                    type="button"
                    variant="contained"
                    startIcon={<AddOutlinedIcon />}
                    onClick={() => {
                        if (clientDetails.length < MAX_ADDITIONAL_CLIENTS) {
                            addClientRow();
                        } else {
                            warningSnackbar(
                                `You can add up to ${MAX_ADDITIONAL_CLIENTS} additional clients.`,
                            );
                        }
                    }}
                    sx={{ width: 'fit-content', textTransform: 'none' }}
                >
                    Add client
                </Button>
            </Stack>
        </NewProspectStep>
    );
}
